import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Editor as TinymceEditor } from 'tinymce';

import type { Service } from 'webapp-types';

import didUpdate from 'webapp/modifiers/did-update';
import FormHtmlEditorFullComponent from 'webapp/components/form/html-editor-full';

interface Args {
	text: string;
	placeholder?: string;
	onChange: (text: string) => void;
	onFocusOut?: (text: string) => void;
	isPreview?: boolean;
}

export default class TeamsTextComponent extends Component<Args> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('team') declare teamService: Service.Team;
	@service('navigation') declare navigationService: Service.Navigation;

	editor?: TinymceEditor;

	get description(): string {
		return this.args.text;
	}

	get placeholderMessage(): string {
		return this.args.placeholder ?? 'Click to add description...';
	}

	get editorOptions() {
		return this.teamService.getEditorOptions(
			this,
			this.args.isPreview,
			this.placeholderMessage,
		);
	}

	@action
	onChange(text: string) {
		this.args.onChange(text);
	}

	@action
	onFocusOut(text: string) {
		this.args.onFocusOut?.(text);
	}

	@action
	onOutsideModelChange() {
		const editor = this.editor;
		//prevent overriding user values if users have the same editor focused
		if (editor?.hasFocus()) {
			return;
		}

		editor?.setContent(this.description);
	}

	@action
	onPreviewChange() {
		const editor = this.editor,
			isReadOnly = this.args.isPreview,
			content = editor?.getContent() || '',
			placeholder = isReadOnly || content.length > 0 ? '' : this.placeholderMessage;

		editor?.setMode(isReadOnly ? 'readonly' : 'design');
		editor?.getBody().setAttribute('data-mce-placeholder', placeholder);
	}
	<template>
		<div
			{{didUpdate this.onOutsideModelChange @text}}
			{{didUpdate this.onPreviewChange @isPreview}}
		>
			<FormHtmlEditorFullComponent
				@text={{this.description}}
				@onChange={{this.onChange}}
				@onFocusOut={{this.onFocusOut}}
				@options={{this.editorOptions}}
				@static={{true}}
			/>
		</div>
	</template>
}
